import { useTranslation } from 'react-i18next';
import { getJstDateString } from '@/utils/businessDate';

type Result<T, E = Error> = { ok: true; value: T } | { ok: false; error: E };

/**
 * 日本標準時(JST)のUTCからのオフセット（ミリ秒）
 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

const pad2 = (value: number) => String(value).padStart(2, '0');

const toJstDate = (date: Date) => new Date(date.getTime() + JST_OFFSET_MS);

const getJstComponents = (date: Date) => {
  const jstDate = toJstDate(date);
  return {
    year: String(jstDate.getUTCFullYear()),
    month: pad2(jstDate.getUTCMonth() + 1),
    day: pad2(jstDate.getUTCDate()),
    hours: pad2(jstDate.getUTCHours()),
    minutes: pad2(jstDate.getUTCMinutes()),
    seconds: pad2(jstDate.getUTCSeconds()),
  };
};

export const useConvertToDate = () => {
  const { t } = useTranslation();

  function tryConvertToDate(str: string): Result<Date, string> {
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;

    if (!datePattern.test(str)) {
      return {
        ok: false,
        error: t('errors.page.invalidDateFormat'),
      };
    }

    const date = new Date(str);

    return { ok: true, value: date };
  }

  /**
   * DateオブジェクトをJSTタイムゾーン付きのISO 8601形式の文字列に変換
   */
  function formatToJstIso8601(date: Date): string {
    const { year, month, day, hours, minutes, seconds } =
      getJstComponents(date);
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+09:00`;
  }

  /**
   * 指定した日数後の日付をJSTで取得
   * @param days - 加算する日数
   * @returns YYYY-MM-DD形式の日付文字列
   */
  function getJstDateStringAfterDays(days: number): string {
    const futureDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    return getJstDateString(futureDate);
  }

  /**
   * ISO8601形式の文字列から日付部分を抽出
   * @param isoString - ISO8601形式の文字列（例: "2025-12-09T00:30:00+09:00"）
   * @returns YYYY-MM-DD形式の日付文字列（例: "2025-12-09"）
   */
  function extractDateFromISO8601(isoString: string): string {
    return isoString.split('T')[0];
  }

  /**
   * ISO8601形式の文字列から時刻部分を抽出
   * @param isoString - ISO8601形式の文字列（例: "2025-12-09T00:30:00+09:00"）
   * @returns HH:MM形式の時刻文字列（例: "00:30"）
   */
  function extractTimeFromISO8601(isoString: string): string {
    const timePart = isoString.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  }

  return {
    tryConvertToDate,
    formatToJstIso8601,
    getJstDateStringAfterDays,
    extractDateFromISO8601,
    extractTimeFromISO8601,
  };
};
